import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRightIcon, ChevronRightIcon } from "lucide-react";
import { CTABand } from "../components/CTABand";
import { Reveal } from "../components/Reveal";
import { WordReveal } from "../components/WordReveal";
import { WhatsAppLine } from "../components/WhatsAppLine";
import { MapleLeaf, MapleWatermark } from "../components/MapleLeaf";
import { IMAGES } from "../data/images";
import { EASE } from "../utils/motion";
const EVENTS = [{
  name: "Track",
  detail: "Sprints and distance races"
}, {
  name: "Relays",
  detail: "The loudest four laps of the day"
}, {
  name: "Football",
  detail: "Soccer on the pitch"
}, {
  name: "Basketball",
  detail: "Fast breaks on the court"
}, {
  name: "Fitness",
  detail: "Challenges for every student"
}];
const PHOTOS = [{
  src: IMAGES.sportsFiesta,
  alt: "Relay runners on the track",
  caption: "Relay final"
}, {
  src: IMAGES.basketball,
  alt: "Students playing basketball",
  caption: "On the court"
}, {
  src: IMAGES.campus,
  alt: "Students gathering on campus",
  caption: "Between events"
}, {
  src: IMAGES.graduation,
  alt: "Students celebrating",
  caption: "The celebration"
}];
function KineticTitle() {
  const ref = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });
  const left = useTransform(scrollYProgress, [0, 1], reduce ? ["0%", "0%"] : ["0%", "-25%"]);
  const right = useTransform(scrollYProgress, [0, 1], reduce ? ["-20%", "-20%"] : ["-20%", "5%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], reduce ? [1, 1] : [1.08, 1.2]);
  return <section ref={ref} className="relative h-screen min-h-[640px] overflow-hidden bg-[#061f38] text-white" data-id="element-1617" data-mp="18pz5ii3jp64p">
      <motion.img style={{
      scale: imgScale
    }} src={IMAGES.sportsFiesta} alt="Students sprinting in a relay at the MCC Sports Fiesta" className="absolute inset-0 h-full w-full object-cover" data-id="element-1618" data-mp="h0m9b971zejq" />
      <div className="absolute inset-0 bg-[#061f38]/55" data-id="element-1619" data-mp="2g3jlsrrr85lx" />
      <div className="relative flex h-full flex-col justify-between pb-10 pt-28" data-id="element-1620" data-mp="2l36074jbjrwb">
        <nav aria-label="Breadcrumb" className="mx-auto flex w-full max-w-[1600px] items-center gap-2 px-5 text-xs text-white/65 md:px-9" data-id="element-1621" data-mp="2pw8mcrlrnz2h">
          <Link to="/" className="hover:text-white" data-id="element-1622" data-mp="2uwd3mjui0xtr">Home</Link>
          <ChevronRightIcon size={13} data-id="element-1623" data-mp="1x7l6xcnp83wu" />
          <Link to="/student-life" className="hover:text-white" data-id="element-1624" data-mp="1x8mm9tbqzckd">Student Life</Link>
          <ChevronRightIcon size={13} data-id="element-1625" data-mp="225v41sszhel3" />
          <span className="text-white/90" data-id="element-1626" data-mp="274zcgh8338ad">Sports Fiesta</span>
        </nav>
        <div aria-hidden="true" className="select-none overflow-hidden whitespace-nowrap text-[clamp(5rem,17vw,16rem)] font-medium leading-[0.82] tracking-[-0.08em]" data-id="element-1627" data-mp="276sh5qz4zifq">
          <motion.div style={{
          x: left
        }} data-id="element-1628" data-mp="3ij34hhv42mcs">SPORTS <span className="text-[#c5233d]" data-id="element-1629" data-mp="3im7dqtnfo8ab">FIESTA</span> SPORTS</motion.div>
          <motion.div style={{
          x: right
        }} className="text-transparent [-webkit-text-stroke:1.5px_rgba(255,255,255,0.7)]" data-id="element-1630" data-mp="3nh4nk2sjelo9">FIESTA SPORTS FIESTA</motion.div>
        </div>
        <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-4 px-5 md:flex-row md:items-end md:justify-between md:px-9" data-id="element-1631" data-mp="16xkflth536hs">
          <h1 className="max-w-xl text-2xl font-medium leading-tight tracking-[-0.03em] md:text-3xl" data-id="element-1632" data-mp="123yg72zzkz3x">The MCC Sports Fiesta: one day, the whole school, all on the field.</h1>
          <p className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.16em] text-[#f0b1b9]" data-id="element-1633" data-mp="s8ejwfhifyen"><MapleLeaf size={11} data-id="element-1634" data-mp="o58h5htlwpcx" /> NEXT FIESTA: DATE TO BE ANNOUNCED</p>
        </div>
      </div>
    </section>;
}
export function SportsFiesta() {
  const reduce = useReducedMotion();
  return <>
      <KineticTitle data-id="element-1635" data-mp="e9euv6q35hhy" />

      <main data-id="element-1636" data-mp="e9yfiv74ejzo">
        <section className="mx-auto grid max-w-[1440px] gap-12 px-5 py-24 md:px-9 lg:grid-cols-12 lg:py-36" data-id="element-1637" data-mp="ea6o4aw7n352">
          <div className="lg:col-span-7" data-id="element-1638" data-mp="28m4p5pkd8spv">
            <p className="text-xs font-semibold tracking-[0.18em] text-[#bb1e35]" data-id="element-1639" data-mp="28n6bwraqv3nf">MORE THAN A SPORTS DAY</p>
            <WordReveal as="p" text="Students compete, cheer, organise and lead. Teachers referee. Families line the track. It is the day the MCC community is loudest." className="mt-6 text-3xl font-medium leading-[1.12] tracking-[-0.045em] md:text-5xl" data-id="element-1640" data-mp="168yrczs3m1r9" />
          </div>
          <Reveal delay={0.15} className="flex flex-col justify-end lg:col-span-4 lg:col-start-9" data-id="element-1641" data-mp="118kri50nhy4b">
            <p className="text-base leading-7 text-[#4f5963]" data-id="element-1642" data-mp="mk7rmwyfzv4j">The Fiesta builds on MCC’s regular extra-curricular sport: basketball, soccer, track and field, and fitness.</p>
            <Link to="/student-life" className="group mt-6 inline-flex w-fit items-center gap-2 border-b border-[#061f38] pb-1 text-sm font-semibold transition-colors duration-200 hover:border-[#bb1e35] hover:text-[#bb1e35]" data-id="element-1643" data-mp="dk1jzrpfdpda">All extra-curricular activities <ArrowRightIcon size={15} className="transition-transform duration-200 group-hover:translate-x-1" data-id="element-1644" data-mp="3mwupj0a9ds9" /></Link>
          </Reveal>
        </section>

        <section className="relative overflow-hidden bg-[#061f38] py-24 text-white md:py-32" data-id="element-1645" data-mp="3l4138y5i06ik">
          <MapleWatermark className="-right-28 top-10 h-[38rem] w-[38rem]" tone="text-white/[0.04]" data-id="element-1646" data-mp="3h2pe0tqjdnaw" />
          <div className="relative mx-auto max-w-[1440px] px-5 md:px-9" data-id="element-1647" data-mp="3v2755frb1cnf">
            <p className="text-xs font-semibold tracking-[0.18em] text-[#f0b1b9]" data-id="element-1648" data-mp="3v0dwxw11mkdb">ON THE PROGRAMME</p>
            <ol className="mt-10 border-t border-white/20" data-id="element-1649" data-mp="3q5gn4mvxw6zd">
              {EVENTS.map((e, i) => <li key={e.name} className="group grid items-baseline gap-2 border-b border-white/20 py-6 md:grid-cols-[90px_1fr_1fr] md:py-7" data-id="element-1650" data-mp="7veirxnivqqh" data-mpi={i}>
                  <span className="text-sm tabular-nums text-[#f0b1b9]" data-id="element-1651" data-mp="hnu1ix6n8jcf">{String(i + 1).padStart(2, "0")}</span>
                  <motion.h2 initial={{
                x: reduce ? 0 : -60,
                opacity: 0
              }} whileInView={{
                x: 0,
                opacity: 1
              }} viewport={{
                once: true,
                amount: 0.6
              }} transition={{
                duration: reduce ? 0.01 : 0.7,
                ease: EASE
              }} className="text-[clamp(3rem,7vw,6rem)] font-medium leading-[0.9] tracking-[-0.07em]" data-id="element-1652" data-mp="cpiu0t3tmkxd">
                    {e.name}
                  </motion.h2>
                  <p className="text-base text-white/60 md:text-right" data-id="element-1653" data-mp="13nj8e8uwzg1u">{e.detail}</p>
                </li>)}
            </ol>
            <p className="mt-6 text-xs text-white/45" data-id="element-1654" data-mp="18gcccuik6kcv">Events vary each year and are confirmed by MCC ahead of the day.</p>
          </div>
        </section>

        <section className="py-24 md:py-32" aria-label="Sports Fiesta photographs" data-id="element-1655" data-mp="1hf65gawokslo">
          <div className="mx-auto flex max-w-[1440px] items-end justify-between px-5 md:px-9" data-id="element-1656" data-mp="1mej21ndcte1g">
            <h2 className="text-4xl font-medium tracking-[-0.06em] md:text-6xl" data-id="element-1657" data-mp="1mcpqpursq5n4">From the sidelines.</h2>
            <p className="hidden text-xs text-[#6b7378] md:block" data-id="element-1658" data-mp="3o5vv9f5ylxho">Swipe or scroll sideways</p>
          </div>
          <div className="no-scrollbar mt-12 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 md:px-9" data-id="element-1659" data-mp="3t4ql312cgmba">
            {PHOTOS.map((p, i) => <motion.figure key={p.caption} initial={{
            opacity: 0,
            y: reduce ? 0 : 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.3
          }} transition={{
            duration: reduce ? 0.01 : 0.6,
            delay: reduce ? 0 : i * 0.08,
            ease: EASE
          }} className={`shrink-0 snap-start ${i % 2 ? "mt-16 w-[70vw] md:w-[28vw]" : "w-[82vw] md:w-[40vw]"}`} data-id="element-1660" data-mp="3ln0ub52yvar6" data-mpi={i}>
                <div className="overflow-hidden" data-id="element-1661" data-mp="3fbbf2zinn03">
                  <img src={p.src} alt={p.alt} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out hover:scale-105" data-id="element-1662" data-mp="3drxhj1c1o1h" />
                </div>
                <figcaption className="mt-3 text-xs text-[#6b7378]" data-id="element-1663" data-mp="3pmt5lpjqvv08">{p.caption}</figcaption>
              </motion.figure>)}
          </div>
          <div className="mx-auto mt-12 flex max-w-[1440px] flex-wrap items-center justify-between gap-6 px-5 md:px-9" data-id="element-1664" data-mp="2e7eaysf0df8a">
            <Link to="/gallery" className="inline-flex items-center gap-2 bg-[#061f38] px-5 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#0c2c4d]" data-id="element-1665" data-mp="33v051z8afp3a"><MapleLeaf size={13} className="text-[#f0b1b9]" data-id="element-1666" data-mp="2uvmu124imsgg" /> Full gallery</Link>
            <WhatsAppLine label="Get the next Fiesta date" context="the next MCC Sports Fiesta date and whether families can attend" data-id="element-1667" data-mp="2uxpw4kiy0c1j" />
          </div>
        </section>
      </main>

      <CTABand eyebrow="JOIN THE TEAM" title="Ambition in the classroom. Energy on the field." image={IMAGES.sportsFiesta} data-id="element-1668" data-mp="3mnb2hdk6auu0" />
    </>;
}